import React, { useState, useCallback, memo, useRef, useImperativeHandle, forwardRef, useEffect } from 'react'
import {
  Card,
  Button,
  Row,
  Col,
  Input,
  Select,
  Typography,
  Checkbox,
  Tooltip,
  DatePicker,
  message,
  Form,
  Alert,
  Space,
  Divider
} from 'antd'
import {
  SearchOutlined,
  ReloadOutlined,
  QuestionCircleOutlined,
  SwapRightOutlined
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { shelfService } from '@services/operator/shelfService'
import { merchantService } from '@services/operator/merchantService'
import { useOperatorData } from '@contexts/operator/OperatorDataContext'
import { HideInStandard } from '@components/operator/shared/StandardGate'
import WarehouseZoneSelect from '@components/shared/warehouses/WarehouseZoneSelect'
import KeywordSearchInput from '@components/shared/ui/KeywordSearchInput'

const { Text } = Typography
const { RangePicker } = DatePicker

/**
 * Left Panel: Filters
 */
const ShelfFilterPanel = memo(function ShelfFilterPanel({ onSearch, loading }) {
  const { t } = useTranslation()
  const { shelfTypes } = useOperatorData()
  const [merchants, setMerchants] = useState([])

  // 使用 listAllWarehouseMerchants 獲取當前倉庫的所有貨主
  useEffect(() => {
    const fetchMerchants = async () => {
      try {
        const result = await merchantService.fetchAllWarehouseMerchants()
        setMerchants(result.data || [])
      } catch (err) {
        console.error('Failed to fetch warehouse merchants:', err)
      }
    }
    fetchMerchants()
  }, [])

  const [filters, setFilters] = useState({
    search: [],
    'shelves.shelf_type_id': [],
    'shelves.temperature': [],
    'shelves.warehouse_zone_id': [],
    merchant_id: [],
    has_inventory: false,
    inventory_updated_at_range: [null, null],
    last_counted_before: null,
    row_start: '',
    row_end: '',
    column_start: '',
    column_end: ''
  })

  const handleFilterChange = (key, val) => {
    setFilters(prev => ({ ...prev, [key]: val }))
  }

  const handleSearch = () => {
    const params = {
      search: filters.search,
      'shelves.shelf_type_id': filters['shelves.shelf_type_id'],
      'shelves.temperature': filters['shelves.temperature'],
      'shelves.warehouse_zone_id': filters['shelves.warehouse_zone_id'],
      merchant_id: filters.merchant_id,
      has_inventory: filters.has_inventory || undefined,
      inventory_updated_at_start: filters.inventory_updated_at_range[0] ? filters.inventory_updated_at_range[0].startOf('day').toISOString() : undefined,
      inventory_updated_at_end: filters.inventory_updated_at_range[1] ? filters.inventory_updated_at_range[1].endOf('day').toISOString() : undefined,
      last_counted_before: filters.last_counted_before ? filters.last_counted_before.endOf('day').toISOString() : undefined,
      row_start: filters.row_start,
      row_end: filters.row_end,
      column_start: filters.column_start,
      column_end: filters.column_end
    }
    onSearch(params)
  }

  const sectionTitleStyle = {
    fontSize: 12,
    color: '#999',
    fontWeight: 500,
    margin: '4px 0 8px',
    letterSpacing: 0.5
  }

  const rangeInputGroup = (startKey, endKey) => (
    <Input.Group compact style={{ display: 'flex', alignItems: 'center' }}>
      <Input
        style={{ flex: 1, textAlign: 'center' }}
        value={filters[startKey]}
        onChange={e => handleFilterChange(startKey, e.target.value)}
      />
      <span style={{ padding: '0 8px', color: '#999' }}>
        <SwapRightOutlined />
      </span>
      <Input
        style={{ flex: 1, textAlign: 'center' }}
        value={filters[endKey]}
        onChange={e => handleFilterChange(endKey, e.target.value)}
      />
    </Input.Group>
  )

  return (
    <Card size="small" bodyStyle={{ padding: '12px 16px' }}>
      <Form layout="vertical" size="small">
        {/* 區段一：基本篩選 */}
        <div style={sectionTitleStyle}>
          {t('shared.stock_takings.new.filter_section_basic')}
        </div>
        <Row gutter={[12, 0]}>
          <Col span={12}>
            <Form.Item label={t('common.action.search')} style={{ marginBottom: 12 }}>
              <KeywordSearchInput
                placeholder={t('shared.stock_takings.new.search_shelf_placeholder')}
                value={filters.search}
                onChange={val => handleFilterChange('search', val)}
                maxCount={5}
              />
            </Form.Item>
          </Col>
          <HideInStandard>
            <Col span={12}>
              <Form.Item label={t('models.merchant.name')} style={{ marginBottom: 12 }}>
                <Select
                  style={{ width: '100%' }}
                  placeholder={t('shared.stock_takings.new.filter_merchant')}
                  mode="multiple"
                  maxTagCount={1}
                  allowClear
                  options={(merchants || []).map(s => ({ label: s.name, value: s.id }))}
                  onChange={val => handleFilterChange('merchant_id', val)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </HideInStandard>
          <Col span={12}>
            <Form.Item label={t('common.resources.warehouse_zones')} style={{ marginBottom: 12 }}>
              <WarehouseZoneSelect
                style={{ width: '100%' }}
                placeholder={t('common.resources.warehouse_zones')}
                mode="multiple"
                maxTagCount={1}
                allowClear
                onChange={val => {
                  if (Array.isArray(val)) {
                    if (val.length > 0 && typeof val[0] === 'object' && 'value' in val[0]) {
                      handleFilterChange('shelves.warehouse_zone_id', val.map(v => v.value))
                    } else {
                      handleFilterChange('shelves.warehouse_zone_id', val)
                    }
                  } else {
                    handleFilterChange('shelves.warehouse_zone_id', [])
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('models.shelf.shelf_type')} style={{ marginBottom: 12 }}>
              <Select
                style={{ width: '100%' }}
                placeholder={t('shared.stock_takings.new.filter_shelf_type')}
                mode="multiple"
                maxTagCount={1}
                allowClear
                options={(shelfTypes || []).map(st => ({ label: st.name, value: st.id }))}
                onChange={val => handleFilterChange('shelves.shelf_type_id', val)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('models.shelf.temperature')} style={{ marginBottom: 12 }}>
              <Select
                style={{ width: '100%' }}
                placeholder={t('shared.stock_takings.new.filter_temperature')}
                mode="multiple"
                maxTagCount={1}
                allowClear
                options={[
                  { label: t('enums.temperature.ambient'), value: 'ambient' },
                  { label: t('enums.temperature.controlled'), value: 'controlled' },
                  { label: t('enums.temperature.refrigerated'), value: 'refrigerated' },
                  { label: t('enums.temperature.frozen'), value: 'frozen' }
                ]}
                onChange={val => handleFilterChange('shelves.temperature', val)}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* 區段二：儲位座標範圍 */}
        <Divider style={{ margin: '4px 0 8px' }} />
        <div style={sectionTitleStyle}>
          <Space size={4}>
            {t('shared.stock_takings.new.filter_section_coordinates')}
            <Tooltip title={t('shared.stock_takings.new.filter_coordinates_tooltip')}>
              <QuestionCircleOutlined style={{ color: '#999', cursor: 'pointer' }} />
            </Tooltip>
          </Space>
        </div>
        <Row gutter={[12, 0]}>
          <Col span={12}>
            <Form.Item label={t('shared.stock_takings.new.filter_row_range')} style={{ marginBottom: 12 }}>
              {rangeInputGroup('row_start', 'row_end')}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('shared.stock_takings.new.filter_column_range')} style={{ marginBottom: 12 }}>
              {rangeInputGroup('column_start', 'column_end')}
            </Form.Item>
          </Col>
        </Row>

        {/* 區段三：時間條件 */}
        <Divider style={{ margin: '4px 0 8px' }} />
        <div style={sectionTitleStyle}>
          {t('shared.stock_takings.new.filter_section_time')}
        </div>
        <Row gutter={[12, 0]}>
          <Col span={12}>
            <Form.Item label={t('shared.stock_takings.new.filter_last_counted_before')} style={{ marginBottom: 12 }}>
              <DatePicker
                style={{ width: '100%' }}
                placeholder={t('common.placeholder.select_date')}
                value={filters.last_counted_before}
                onChange={val => handleFilterChange('last_counted_before', val)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('shared.stock_takings.new.filter_inventory_moved')} style={{ marginBottom: 12 }}>
              <RangePicker
                style={{ width: '100%' }}
                placeholder={[t('common.placeholder.start_date'), t('common.placeholder.end_date')]}
                value={filters.inventory_updated_at_range}
                onChange={val => handleFilterChange('inventory_updated_at_range', val)}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: '4px 0 12px' }} />
        <Row gutter={[12, 0]} align="middle">
          <Col span={12}>
            <Checkbox
              checked={filters.has_inventory}
              onChange={e => handleFilterChange('has_inventory', e.target.checked)}
            >
              {t('shared.stock_takings.new.filter_has_inventory')}
            </Checkbox>
          </Col>
          <Col span={12} style={{ textAlign: 'right' }}>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading}>
              {t('common.action.search')}
            </Button>
          </Col>
        </Row>
      </Form>
    </Card>
  )
})

/**
 * Single Shelf Item - fully controls its own state, no parent re-render
 */
const ShelfListItem = memo(function ShelfListItem({ item, defaultChecked, onToggle }) {
  const [checked, setChecked] = useState(defaultChecked)

  const handleChange = (e) => {
    const newChecked = e.target.checked
    setChecked(newChecked)
    onToggle(item.id, newChecked)
  }

  return (
    <label
      style={{
        padding: '6px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
        background: checked ? '#e6f4ff' : 'transparent'
      }}
    >
      <Checkbox checked={checked} onChange={handleChange} />
      <Text code style={{ fontSize: 12 }}>{item.location}</Text>
    </label>
  )
})

/**
 * Right Panel: Selection List
 * Uses ref to expose getSelectedIds() - no state updates on toggle
 */
const ShelfSelectionList = forwardRef(function ShelfSelectionList({
  data,
  loading,
  onRefresh,
  onCountChange
}, ref) {
  const { t } = useTranslation()
  const [listSearch, setListSearch] = useState('')
  const [listKey, setListKey] = useState(0)
  const [selectedCount, setSelectedCount] = useState(0)

  // Use ref to store selection state - no re-renders on change
  const selectedMapRef = useRef(new Map())

  // Expose getSelectedIds to parent
  useImperativeHandle(ref, () => ({
    getSelectedIds: () => {
      const ids = []
      selectedMapRef.current.forEach((checked, id) => {
        if (checked) ids.push(id)
      })
      return ids
    }
  }))

  // Initialize when data changes
  React.useEffect(() => {
    const newMap = new Map()
    data.forEach(item => {
      newMap.set(item.id, true) // Default all selected
    })
    selectedMapRef.current = newMap
    setSelectedCount(data.length)
    onCountChange?.(data.length)
    setListKey(prev => prev + 1)
  }, [data, onCountChange])

  const filteredData = listSearch
    ? data.filter(item => item.location.toLowerCase().includes(listSearch.toLowerCase()))
    : data

  // Just update ref, no state change = no re-render
  const handleToggle = useCallback((id, checked) => {
    selectedMapRef.current.set(id, checked)
    // Only update count (lightweight)
    let count = 0
    selectedMapRef.current.forEach(v => { if (v) count++ })
    setSelectedCount(count)
    onCountChange?.(count)
  }, [onCountChange])

  const handleSelectAll = () => {
    filteredData.forEach(item => {
      selectedMapRef.current.set(item.id, true)
    })
    let count = 0
    selectedMapRef.current.forEach(v => { if (v) count++ })
    setSelectedCount(count)
    onCountChange?.(count)
    setListKey(prev => prev + 1)
  }

  const handleClearAll = () => {
    selectedMapRef.current.forEach((_, id) => {
      selectedMapRef.current.set(id, false)
    })
    setSelectedCount(0)
    onCountChange?.(0)
    setListKey(prev => prev + 1)
  }

  const handleInvert = () => {
    filteredData.forEach(item => {
      const cur = selectedMapRef.current.get(item.id) ?? false
      selectedMapRef.current.set(item.id, !cur)
    })
    let count = 0
    selectedMapRef.current.forEach(v => { if (v) count++ })
    setSelectedCount(count)
    onCountChange?.(count)
    setListKey(prev => prev + 1)
  }

  const allChecked = filteredData.length > 0 && filteredData.every(item => selectedMapRef.current.get(item.id))
  const someChecked = filteredData.some(item => selectedMapRef.current.get(item.id))

  return (
    <Card size="small" bodyStyle={{ padding: 0 }}>
      {/* Header */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Checkbox
            checked={allChecked}
            indeterminate={!allChecked && someChecked}
            onChange={e => e.target.checked ? handleSelectAll() : handleClearAll()}
          />
          <Text strong>{t('common.label.selected')} ({selectedCount} / {data.length})</Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {data.length > 0 && (
            <Button type="link" size="small" onClick={handleInvert} style={{ padding: 0 }}>
              {t('shared.stock_takings.new.action_invert_selection')}
            </Button>
          )}
          {selectedCount > 0 && (
            <Button type="link" danger size="small" onClick={handleClearAll} style={{ padding: 0 }}>
              {t('common.action.clear_all')}
            </Button>
          )}
          <Tooltip title={t('common.action.refresh')}>
            <ReloadOutlined onClick={onRefresh} style={{ cursor: 'pointer', color: '#1890ff' }} />
          </Tooltip>
        </div>
      </div>

      {/* Search */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Input
          placeholder={t('common.action.search')}
          prefix={<SearchOutlined />}
          value={listSearch}
          onChange={e => setListSearch(e.target.value)}
          allowClear
          size="small"
        />
      </div>

      {/* Warning */}
      {selectedCount > 300 && (
        <Alert
          message={t('shared.stock_takings.new.limit_warning', { count: selectedCount })}
          type="warning"
          showIcon
          banner
        />
      )}

      {/* List */}
      <div style={{ minHeight: 400, maxHeight: 'calc(100vh - 360px)', overflowY: 'auto', padding: '4px 0' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>{t('common.loading')}</div>
        ) : filteredData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            {data.length === 0 ? t('common.label.no_data') : t('common.label.no_results')}
          </div>
        ) : (
          filteredData.map(item => (
            <ShelfListItem
              key={`${item.id}-${listKey}`}
              item={item}
              defaultChecked={selectedMapRef.current.get(item.id) ?? true}
              onToggle={handleToggle}
            />
          ))
        )}
      </div>

      {/* Footer */}
      {data.length > 0 && (
        <div style={{ padding: '8px 12px', borderTop: '1px solid #f0f0f0', textAlign: 'center', color: '#999', fontSize: 12 }}>
          {listSearch && filteredData.length !== data.length
            ? `${filteredData.length} / ${data.length} ${t('common.label.items')}`
            : `${data.length} ${t('common.label.items')}`
          }
        </div>
      )}
    </Card>
  )
})

/**
 * Main Component: Shelf Selection for Stock Taking
 *
 * Usage:
 *   const ref = useRef()
 *   <ShelfTransferSelector ref={ref} />
 *   // Get selected IDs when needed:
 *   const ids = ref.current?.getSelectedIds()
 */
const ShelfTransferSelector = forwardRef(function ShelfTransferSelector({ style, onCountChange }, ref) {
  const { t } = useTranslation()

  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [lastParams, setLastParams] = useState(null)

  const listRef = useRef()

  // Expose getSelectedIds to parent
  useImperativeHandle(ref, () => ({
    getSelectedIds: () => listRef.current?.getSelectedIds() ?? []
  }))

  const fetchData = useCallback(async (params) => {
    setLoading(true)
    setLastParams(params)
    try {
      const response = await shelfService.listAll(params)
      if (response && response.data) {
        setData(response.data)
      }
    } catch (error) {
      console.error(error)
      message.error(t('common.error.load_failed'))
    } finally {
      setLoading(false)
    }
  }, [t])

  const handleRefresh = useCallback(() => {
    if (lastParams) {
      fetchData(lastParams)
    }
  }, [lastParams, fetchData])

  return (
    <div style={style}>
      <Row gutter={16}>
        <Col xs={24} md={14}>
          <ShelfFilterPanel onSearch={fetchData} loading={loading} />
        </Col>
        <Col xs={24} md={10}>
          <ShelfSelectionList
            ref={listRef}
            data={data}
            loading={loading}
            onRefresh={handleRefresh}
            onCountChange={onCountChange}
          />
        </Col>
      </Row>
    </div>
  )
})

export default ShelfTransferSelector
